import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { FilterInformationsService } from '@shared/services/api/filter-informations.service';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Component({
  selector: 'app-analytics-filter',
  templateUrl: './analytics-filter.component.html',
  styleUrl: './analytics-filter.component.scss'
})
export class AnalyticsFilterComponent implements OnInit, OnChanges {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() name = '';
  @Input() displayFields: string[] = [];
  @Input() initialSelectedValues: any[] = [];
  @Output() selectedFilter = new EventEmitter<{ name: string; value: any[] }>();

  selectedValues: any[] = [];

  constructor(private _filterInfoService: FilterInformationsService) {}

  ngOnInit(): void {
    this.initializeSelectedValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialSelectedValues'] && !changes['initialSelectedValues'].firstChange) {
      // Only update if values actually changed
      const newValues = changes['initialSelectedValues'].currentValue || [];
      const currentValues = this.selectedValues || [];

      if (JSON.stringify(newValues) !== JSON.stringify(currentValues)) {
        this.initializeSelectedValues();
      }
    }
  }

  private initializeSelectedValues(): void {
    if (this.initialSelectedValues && this.initialSelectedValues.length > 0) {
      // Transform simple values into objects with id if needed
      this.selectedValues = this.initialSelectedValues.map((value, index) => {
        if (typeof value === 'string') {
          return { id: value, [this.name]: value };
        }
        if (value && typeof value === 'object' && !value.id) {
          return { ...value, id: value[this.name] || index };
        }
        return value;
      });
    } else {
      this.selectedValues = [];
    }
  }

  filterFetcher = (searchTerm: string) => {
    return this._filterInfoService.getFilterValues([this.name], { value: searchTerm }, '15').pipe(
      map((results: any[]) => {
        // Ensure each result has an id field
        return results.map((item: any, index: number) => {
          if (item && typeof item === 'object') {
            if (!item.id) {
              return { ...item, id: item[this.name] || index };
            }
            return item;
          }
          // If it's a simple value, convert to object
          return { id: item, [this.name]: item };
        });
      }),
      catchError((error) => {
        console.error('Error fetching filter values:', error);
        return of([]);
      })
    );
  };

  onSelectedValuesChange(values: any[]): void {
    // Prevent emitting if values haven't actually changed
    if (JSON.stringify(this.selectedValues) === JSON.stringify(values)) {
      return;
    }

    this.selectedValues = values;
    this.emitSelectedValues();
  }

  private emitSelectedValues(): void {
    const simplifiedValues = this.selectedValues.map((value) => {
      if (typeof value === 'string') return value;

      if (value && typeof value === 'object') {
        if (this.name in value) {
          return value[this.name];
        }

        for (const field of this.displayFields) {
          if (field in value) {
            return value[field];
          }
        }

        const firstKey = Object.keys(value)[0];
        if (firstKey) {
          return value[firstKey];
        }
      }

      return value;
    });

    this.selectedFilter.emit({
      name: this.name,
      value: simplifiedValues
    });
  }
}
